import React, { useState } from 'react';
import { Dependant, AgeCategory, YouthChildrenSubCategory, PrimaryCamper } from '../../../types/camper';
import './OccupantsForm.css';

interface OccupantsFormProps {
  primaryCamperId: string;
  primaryCamper: Partial<PrimaryCamper>;
  initialOccupants?: Dependant[];
  onSubmit: (occupants: Partial<Dependant>[]) => Promise<void>;
  isReadOnly?: boolean;
}

const OccupantsForm: React.FC<OccupantsFormProps> = ({
  primaryCamperId,
  primaryCamper,
  initialOccupants = [],
  onSubmit,
  isReadOnly = false
}) => {
  const [occupants, setOccupants] = useState<Partial<Dependant>[]>(initialOccupants);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Helper function to check if occupants have changed
  const checkForChanges = (updatedOccupants: Partial<Dependant>[]) => {
    if (updatedOccupants.length !== initialOccupants.length) {
      setHasChanges(true);
      return;
    }

    const hasChanges = updatedOccupants.some((occupant, index) => {
      const initial = initialOccupants[index];
      return (
        occupant.FirstName !== initial.FirstName ||
        occupant.LastName !== initial.LastName ||
        occupant.Age !== initial.Age ||
        (occupant.Age === AgeCategory.YouthChildren && occupant.YouthChildrenCategory !== initial.YouthChildrenCategory) ||
        (initial.Age === AgeCategory.YouthChildren && initial.YouthChildrenCategory !== occupant.YouthChildrenCategory)
      );
    });

    setHasChanges(hasChanges);
  };

  const handleOccupantChange = (index: number, field: keyof Dependant, value: string | AgeCategory | YouthChildrenSubCategory | undefined) => {
    const updatedOccupants = occupants.map((occupant, i) => {
      if (i === index) {
        const updatedOccupant = { ...occupant };
        
        // Handle Age field specially
        if (field === 'Age') {
          updatedOccupant.Age = value as AgeCategory;
          // Clear YouthChildrenCategory if not Youth/Children
          if (value !== AgeCategory.YouthChildren) {
            updatedOccupant.YouthChildrenCategory = undefined;
          }
        } else if (field === 'YouthChildrenCategory') {
          updatedOccupant.YouthChildrenCategory = value as YouthChildrenSubCategory;
        } else {
          updatedOccupant[field] = value as string;
        }
        
        return updatedOccupant;
      }
      return occupant;
    });

    setOccupants(updatedOccupants);
    checkForChanges(updatedOccupants);
  };

  const handleAddOccupant = () => {
    const newOccupant = {
      FirstName: '',
      LastName: '',
      Age: AgeCategory.MustChange
    };
    const updatedOccupants = [...occupants, newOccupant];
    setOccupants(updatedOccupants);
    setHasChanges(true);
  };

  const handleRemoveOccupant = (index: number) => {
    const updatedOccupants = occupants.filter((_, i) => i !== index);
    setOccupants(updatedOccupants);
    checkForChanges(updatedOccupants);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Only validate that Youth/Children have a subcategory
      const isValid = occupants.every(occupant => {
        if (occupant.Age === AgeCategory.YouthChildren) {
          return !!occupant.YouthChildrenCategory;
        }
        return true;
      });

      if (!isValid) {
        setError('Please select a grade level for all Youth/Children occupants');
        return;
      }

      // Clean up occupants data before saving
      const cleanOccupants = occupants.map(occupant => {
        const cleanOccupant: Partial<Dependant> = { ...occupant };
        
        // Remove undefined values
        (Object.keys(cleanOccupant) as Array<keyof Partial<Dependant>>).forEach(key => {
          if (cleanOccupant[key] === undefined) {
            delete cleanOccupant[key];
          }
        });

        return cleanOccupant;
      });

      await onSubmit(cleanOccupants);
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving occupants:', err);
      setError('Failed to save occupants');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="occupants-form">
      <h2>Occupants</h2>
      {error && <div className="error-message">{error}</div>}

      <div className="occupants-list">
        <div className="occupants-header">
          <div>First Name</div>
          <div>Last Name</div>
          <div>Age Category</div>
          <div></div>
        </div>

        {/* Primary Camper */}
        <div className="occupant-row primary-camper">
          <div>{primaryCamper.FirstName || ''}</div>
          <div>{primaryCamper.LastName || ''}</div>
          <div>{primaryCamper.Age || ''}</div>
          <div className="role-label">Primary</div>
        </div>

        {/* Additional Occupants */}
        {occupants.map((occupant, index) => (
          <div key={index} className="occupant-row">
            <input
              type="text"
              value={occupant.FirstName}
              onChange={(e) => handleOccupantChange(index, 'FirstName', e.target.value)}
              placeholder="First Name"
              required
              disabled={isReadOnly}
            />
            <input
              type="text"
              value={occupant.LastName}
              onChange={(e) => handleOccupantChange(index, 'LastName', e.target.value)}
              placeholder="Last Name"
              required
              disabled={isReadOnly}
            />
            <div className="age-selects">
              <select
                value={occupant.Age || ''}
                onChange={(e) => handleOccupantChange(index, 'Age', e.target.value as AgeCategory)}
                required
                disabled={isReadOnly}
              >
                <option value="">Select Age Category</option>
                <option value={AgeCategory.MustChange}>Must Change</option>
                <option value={AgeCategory.Adult}>Adult</option>
                <option value={AgeCategory.AdultStudent}>Adult Student</option>
                <option value={AgeCategory.YouthChildren}>Youth/Children</option>
              </select>
              
              {occupant.Age === AgeCategory.YouthChildren && (
                <select
                  value={occupant.YouthChildrenCategory || ''}
                  onChange={(e) => handleOccupantChange(index, 'YouthChildrenCategory', e.target.value as YouthChildrenSubCategory)}
                  required
                  disabled={isReadOnly}
                >
                  <option value="">Select Grade Level</option>
                  {Object.values(YouthChildrenSubCategory).map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {!isReadOnly && (
              <button
                type="button"
                className="remove-button"
                onClick={() => handleRemoveOccupant(index)}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>

      {!isReadOnly && (
        <div className="form-actions">
          <button type="button" onClick={handleAddOccupant} className="add-button">
            Add Occupant
          </button>
          {hasChanges && (
            <button type="submit" className="submit-button">
              Save Changes
            </button>
          )}
        </div>
      )}
    </form>
  );
};

export default OccupantsForm; 